import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { Box } from "@mui/material";

import CustomButton from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import TabComponent from "components/common/Tabs";
import CustomSnackbar, { AlertType } from "components/common/Snackbar";

import { getReferralEnrollmentStatus, joinReferralProgram, ReferralRockMember } from "services/referral";

import CustomerReferralHeaderSvg from "assets/images/customerReferralHeader.svg";

import ReferBusiness from "./ReferBusiness";
import MyReferrals from "./MyReferrals";
import HowItWorks from "./HowItWorks";
import FAQ from "./FAQ";

import "./Referral.scss";

function ReferralPage() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [memberData, setMemberData] = useState<ReferralRockMember | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: AlertType.success });

  const tabs = [{ label: "Refer a business" }, { label: "My referrals" }];

  useEffect(() => {
    checkEnrollmentStatus();
  }, []);

  useEffect(() => {
    setActiveTab(0);
  }, [location.key]);

  const checkEnrollmentStatus = async () => {
    setIsLoading(true);
    const response = await getReferralEnrollmentStatus();
    if (response.ok) {
      setIsEnrolled(response.payload.isEnrolled);
      if (response.payload.isEnrolled && response.payload.memberData) {
        setMemberData(response.payload.memberData);
      }
    }
    setIsLoading(false);
  };

  const handleJoinProgram = async () => {
    setIsJoining(true);
    try {
      const response = await joinReferralProgram();
      if (response.ok) {
        setIsEnrolled(true);
        if (response.payload.memberData) {
          setMemberData(response.payload.memberData);
        }
        setSnackbar({ open: true, message: "Welcome to Edge Referral Program!", type: AlertType.success });
      } else {
        setSnackbar({ open: true, message: "Failed to join the program. Please try again.", type: AlertType.error });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "An error occurred. Please try again.", type: AlertType.error });
    } finally {
      setIsJoining(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="referral-page">
      <div className="referral-header-section">
        <div className="referral-header-content">
          <h1 className="referral-title">
            <span className="highlight">Earn up to $2,000</span> for helping
            <br />
            others hire smarter
          </h1>
          <p className="referral-subtitle">
            Know someone who could use reliable, day-one-ready talent? Refer them and earn a $2,000 reward when they
            hire. They'll get an exclusive referral discount of $2,000.
          </p>
          {!isEnrolled && (
            <CustomButton
              label="Join the program"
              onClick={handleJoinProgram}
              isLoading={isJoining}
              isDisabled={isJoining}
              variant="contained"
              color="primary"
              size="large"
              className="join-program-button"
            />
          )}
        </div>
        <div className="referral-header-image">
          <img src={CustomerReferralHeaderSvg} alt="Referral" />
        </div>
      </div>

      {!isEnrolled && (
        <>
          <HowItWorks />
          <FAQ />
        </>
      )}

      {isEnrolled && (
        <div className="referral-content-card">
          <div className="referral-tabs-container">
            <TabComponent tabs={tabs} tab={activeTab} setTab={setActiveTab} />
          </div>

          <Box>
            {activeTab === 0 && <ReferBusiness isEnrolled={isEnrolled} memberData={memberData} />}
            {activeTab === 1 && <MyReferrals memberData={memberData} />}
          </Box>
        </div>
      )}

      {isEnrolled && activeTab === 0 && (
        <>
          <HowItWorks />
          <FAQ />
        </>
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
}

export default ReferralPage;
